import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Wallet, Save, ArrowRightLeft, CreditCard, User, Check, X as XIcon } from 'lucide-react';
import api from '../../lib/api';
import { validarRutChileno, formatearRut } from '../../lib/rut';

const CATEGORIAS = [
  { val: 'transferencia_anticipo', label: 'Anticipo por Transferencia', icon: ArrowRightLeft, color: 'text-blue-600' },
  { val: 'tarjeta_credito',        label: 'Tarjeta de Crédito',          icon: CreditCard,     color: 'text-purple-600' },
  { val: 'rendicion',              label: 'Rendición (Caja Chica)',      icon: User,           color: 'text-emerald-600' },
  { val: 'otro',                   label: 'Otro',                         icon: Wallet,         color: 'text-gray-600' },
];

const FLAGS = [
  { key: 'habilitado_oc_cxp_nominas_viernes',     label: 'OC CxP Nóminas Vie.',   desc: 'Nóminas de pago a proveedores (viernes)' },
  { key: 'habilitado_rendicion_gastos',           label: 'Rendición Gastos',      desc: 'Módulo de rendición de gastos' },
  { key: 'habilitado_tarjetas_credito',           label: 'Tarjetas de Crédito',   desc: 'Informes y listados de Tarjetas de Crédito' },
];

const empty = {
  codigo: '', nombre: '', categoria: 'otro', banco: '', persona_asociada: '',
  // Iter 313.j.4 (Renzo, feb-2026): RUT de la persona asociada al medio,
  // usado como contacto en Alegra al registrar el pago de una rendición.
  rut_persona: '',
  // ERP y Alegra son implícitos: crear en el ERP siempre implica sincronizar con Alegra.
  habilitado_erp: true, habilitado_alegra: true,
  habilitado_oc_cxp_nominas_viernes: false, habilitado_rendicion_gastos: false,
  habilitado_tarjetas_credito: false,
  observaciones: '', activo: true,
  // Iter 286.h — mapeo con Alegra
  alegra_payment_method: '', alegra_bank_account_id: null, alegra_bank_account_name: '',
};

export default function MediosPagoPage() {
  const [medios, setMedios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incluirInactivos, setIncluirInactivos] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editCodigo, setEditCodigo] = useState(null);
  // Iter 286.h — catálogos Alegra
  const [alegraPaymentMethods, setAlegraPaymentMethods] = useState([]);
  const [alegraBankAccounts, setAlegraBankAccounts] = useState([]);
  const [loadingAlegra, setLoadingAlegra] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/medios-pago', { params: { incluir_inactivos: incluirInactivos } });
      setMedios(res.data || []);
    } catch {
      toast.error('Error cargando medios de pago');
    } finally {
      setLoading(false);
    }
  }, [incluirInactivos]);

  useEffect(() => { load(); }, [load]);

  // Iter 286.h — carga catálogos Alegra la primera vez que se abre el diálogo
  const loadAlegraCatalogs = useCallback(async () => {
    if (alegraPaymentMethods.length > 0 && alegraBankAccounts.length > 0) return;
    setLoadingAlegra(true);
    try {
      const [pm, ba] = await Promise.all([
        api.get('/medios-pago/alegra/payment-methods').catch(() => ({ data: [] })),
        api.get('/medios-pago/alegra/bank-accounts').catch(() => ({ data: [] })),
      ]);
      setAlegraPaymentMethods(pm.data || []);
      setAlegraBankAccounts(ba.data || []);
    } finally {
      setLoadingAlegra(false);
    }
  }, [alegraPaymentMethods.length, alegraBankAccounts.length]);

  const refreshAlegraAccounts = async () => {
    setLoadingAlegra(true);
    try {
      const res = await api.get('/medios-pago/alegra/bank-accounts', { params: { refresh: true } });
      setAlegraBankAccounts(res.data || []);
      toast.success(`${(res.data || []).length} cuentas Alegra actualizadas`);
    } catch {
      toast.error('Error refrescando cuentas Alegra');
    } finally {
      setLoadingAlegra(false);
    }
  };

  const mediosFiltrados = useMemo(() => {
    if (filtroCategoria === 'todas') return medios;
    return medios.filter((m) => m.categoria === filtroCategoria);
  }, [medios, filtroCategoria]);

  // Agrupamos por categoría para un listado con secciones claras
  const gruposPorCategoria = useMemo(() => {
    const grupos = {};
    for (const m of mediosFiltrados) {
      const cat = m.categoria || 'otro';
      if (!grupos[cat]) grupos[cat] = [];
      grupos[cat].push(m);
    }
    return grupos;
  }, [mediosFiltrados]);

  const openNew = () => {
    setForm({ ...empty });
    setEditCodigo(null);
    setShowDialog(true);
    loadAlegraCatalogs();
  };

  const openEdit = (m) => {
    setForm({
      codigo: m.codigo || '',
      nombre: m.nombre || '',
      categoria: m.categoria || 'otro',
      banco: m.banco || '',
      persona_asociada: m.persona_asociada || '',
      rut_persona: m.rut_persona || '',  // Iter 313.j.4
      // ERP y Alegra: implícitos, siempre true (crear en ERP implica sincronizar con Alegra)
      habilitado_erp: true,
      habilitado_alegra: true,
      habilitado_oc_cxp_nominas_viernes: !!m.habilitado_oc_cxp_nominas_viernes,
      habilitado_rendicion_gastos: !!m.habilitado_rendicion_gastos,
      habilitado_tarjetas_credito: !!m.habilitado_tarjetas_credito,
      observaciones: m.observaciones || '',
      activo: m.activo !== false,
      // Iter 286.h — mapeo Alegra
      alegra_payment_method: m.alegra_payment_method || '',
      alegra_bank_account_id: m.alegra_bank_account_id || null,
      alegra_bank_account_name: m.alegra_bank_account_name || '',
    });
    setEditCodigo(m.codigo);
    setShowDialog(true);
    loadAlegraCatalogs();
  };

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) {
      toast.error('Código y nombre son obligatorios');
      return;
    }
    // Gobernanza RUT (Fase 1): el RUT de la persona pasa por el subprograma único
    // (módulo 11 + formato 00000000-A). Si es inválido, avisamos y no dejamos guardar.
    let rutPersona = (form.rut_persona || '').trim();
    if (rutPersona) {
      if (!validarRutChileno(rutPersona)) {
        toast.error('El RUT de la persona no es válido (dígito verificador módulo 11). Revísalo y vuelve a ingresarlo.');
        return;
      }
      rutPersona = formatearRut(rutPersona) || rutPersona;
    }
    const payload = {
      ...form,
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      rut_persona: rutPersona,
    };
    try {
      if (editCodigo) {
        await api.put(`/medios-pago/${encodeURIComponent(editCodigo)}`, payload);
        toast.success(`Medio de pago "${payload.nombre}" actualizado`);
      } else {
        await api.post('/medios-pago', payload);
        toast.success(`Medio de pago "${payload.nombre}" creado`);
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleFlag = async (medio, flagKey) => {
    // Actualización directa desde la tabla (checkbox toggle)
    const payload = {
      codigo: medio.codigo,
      nombre: medio.nombre,
      categoria: medio.categoria,
      banco: medio.banco || '',
      persona_asociada: medio.persona_asociada || '',
      rut_persona: medio.rut_persona || '',  // Iter 313.j.4
      // ERP y Alegra siempre true (implícito)
      habilitado_erp: true,
      habilitado_alegra: true,
      habilitado_oc_cxp_nominas_viernes: !!medio.habilitado_oc_cxp_nominas_viernes,
      habilitado_rendicion_gastos: !!medio.habilitado_rendicion_gastos,
      habilitado_tarjetas_credito: !!medio.habilitado_tarjetas_credito,
      observaciones: medio.observaciones || '',
      activo: medio.activo !== false,
      // Iter 286.h — preservar mapeo Alegra al hacer toggle rápido de flags
      alegra_payment_method: medio.alegra_payment_method || '',
      alegra_bank_account_id: medio.alegra_bank_account_id || null,
      alegra_bank_account_name: medio.alegra_bank_account_name || '',
      [flagKey]: !medio[flagKey],
    };
    try {
      await api.put(`/medios-pago/${encodeURIComponent(medio.codigo)}`, payload);
      setMedios((prev) => prev.map((m) => (m.codigo === medio.codigo ? { ...m, ...payload } : m)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleToggleActive = async (m) => {
    try {
      if (m.activo === false) {
        await api.post(`/medios-pago/${encodeURIComponent(m.codigo)}/reactivar`);
        toast.success(`"${m.nombre}" reactivado`);
      } else {
        if (!window.confirm(`¿Desactivar "${m.nombre}"? Ya no aparecerá en las selecciones.`)) return;
        await api.delete(`/medios-pago/${encodeURIComponent(m.codigo)}`);
        toast.success(`"${m.nombre}" desactivado`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="space-y-5" data-testid="medios-pago-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-indigo-600" />
            Medios de Pago
          </h1>
          <p className="text-sm text-muted-foreground">
            Instrumentos con los que se paga (el <b>CÓMO</b>). No confundir con Condiciones de Pago (el <b>CUÁNDO</b> — plazo comercial).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
            <SelectTrigger className="w-56" data-testid="filtro-categoria">
              <SelectValue placeholder="Filtrar categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las categorías</SelectItem>
              {CATEGORIAS.map((c) => (
                <SelectItem key={c.val} value={c.val}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={incluirInactivos}
              onChange={(e) => setIncluirInactivos(e.target.checked)}
              className="accent-indigo-600"
              data-testid="chk-incluir-inactivos"
            />
            Mostrar inactivos
          </label>
          <Button onClick={openNew} className="gap-2" data-testid="btn-nuevo-medio">
            <Plus className="h-4 w-4" /> Nuevo Medio de Pago
          </Button>
        </div>
      </div>

      {/* Leyenda de flags */}
      <Card className="bg-slate-50 dark:bg-slate-900/40 border-slate-200">
        <CardContent className="py-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            {FLAGS.map((f) => (
              <div key={f.key}>
                <div className="font-semibold text-slate-700 dark:text-slate-200">{f.label}</div>
                <div className="text-slate-500">{f.desc}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Cargando…</CardContent></Card>
      ) : mediosFiltrados.length === 0 ? (
        <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">
          No hay medios de pago. Presioná &quot;Nuevo Medio de Pago&quot; para agregar el primero.
        </CardContent></Card>
      ) : (
        CATEGORIAS.filter((c) => gruposPorCategoria[c.val]?.length).map((cat) => {
          const Icon = cat.icon;
          const items = gruposPorCategoria[cat.val] || [];
          return (
            <Card key={cat.val}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${cat.color}`} />
                  {cat.label} <Badge variant="outline" className="ml-1">{items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-40">Código</TableHead>
                        <TableHead>Nombre</TableHead>
                        {cat.val === 'transferencia_anticipo' || cat.val === 'tarjeta_credito' ? (
                          <TableHead>Banco</TableHead>
                        ) : cat.val === 'rendicion' ? (
                          <TableHead>Persona</TableHead>
                        ) : (
                          <TableHead>Info</TableHead>
                        )}
                        {FLAGS.map((f) => (
                          <TableHead key={f.key} className="text-center text-xs">{f.label}</TableHead>
                        ))}
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((m) => (
                        <TableRow key={m.codigo} className={m.activo === false ? 'opacity-60' : ''} data-testid={`row-medio-${m.codigo}`}>
                          <TableCell className="font-mono text-xs">{m.codigo}</TableCell>
                          <TableCell className="font-medium">
                            {m.nombre}
                            {/* Iter 286.h — indicador de mapeo Alegra */}
                            {m.alegra_payment_method && m.alegra_bank_account_id ? (
                              <span
                                className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                                title={`Alegra: ${m.alegra_payment_method} → ${m.alegra_bank_account_name} (#${m.alegra_bank_account_id})`}
                                data-testid={`alegra-map-${m.codigo}`}
                              >
                                <ArrowRightLeft className="h-2.5 w-2.5" />
                                Alegra
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-xs">
                            {cat.val === 'rendicion' ? (
                              <div>
                                <div>{m.persona_asociada || '—'}</div>
                                {/* Iter 313.j.4 — RUT visible en categoría rendición */}
                                {m.rut_persona && (
                                  <div className="text-[10px] text-muted-foreground font-mono">{m.rut_persona}</div>
                                )}
                              </div>
                            ) : (m.banco || '—')}
                          </TableCell>
                          {FLAGS.map((f) => (
                            <TableCell key={f.key} className="text-center">
                              <button
                                type="button"
                                title={`${m[f.key] ? 'Habilitado' : 'Deshabilitado'} — click para cambiar`}
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition ${
                                  m[f.key]
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300'
                                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500'
                                }`}
                                onClick={() => handleToggleFlag(m, f.key)}
                                disabled={m.activo === false}
                                data-testid={`flag-${m.codigo}-${f.key}`}
                              >
                                {m[f.key] ? <Check className="h-3.5 w-3.5" /> : <XIcon className="h-3.5 w-3.5" />}
                              </button>
                            </TableCell>
                          ))}
                          <TableCell className="text-center">
                            {m.activo === false ? (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">Inactivo</Badge>
                            ) : (
                              <Badge className="bg-green-600 hover:bg-green-700">Activo</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`btn-edit-${m.codigo}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(m)}
                                className={m.activo === false ? 'text-emerald-600' : 'text-orange-600'}
                                data-testid={`btn-toggle-${m.codigo}`}
                              >
                                {m.activo === false ? 'Reactivar' : 'Desactivar'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Modal Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-medio-pago">
          <DialogHeader>
            <DialogTitle>{editCodigo ? 'Editar Medio de Pago' : 'Nuevo Medio de Pago'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Código *</Label>
                <Input
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  placeholder="Ej: MP-TC-BCHILE-VISA"
                  disabled={!!editCodigo}
                  data-testid="input-codigo"
                />
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="input-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((c) => (
                      <SelectItem key={c.val} value={c.val}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Tarjeta Banco de Chile Visa"
                data-testid="input-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Banco</Label>
                <Input
                  value={form.banco}
                  onChange={(e) => setForm({ ...form, banco: e.target.value })}
                  placeholder="Ej: Banco de Chile"
                  data-testid="input-banco"
                />
              </div>
              <div>
                <Label>Persona asociada <span className="text-xs text-muted-foreground">(solo rendiciones)</span></Label>
                <Input
                  value={form.persona_asociada}
                  onChange={(e) => setForm({ ...form, persona_asociada: e.target.value })}
                  placeholder="Ej: Álvaro Jara"
                  data-testid="input-persona"
                />
              </div>
            </div>

            {/* Iter 313.j.4 — RUT de la persona (obligatorio para rendiciones, se usa como contacto en Alegra) */}
            {form.categoria === 'rendicion' && (
              <div>
                <Label>
                  RUT persona <span className="text-red-500">*</span>
                  <span className="text-xs text-muted-foreground ml-1">
                    (se usa como contacto en Alegra al pagar la rendición)
                  </span>
                </Label>
                <Input
                  value={form.rut_persona || ''}
                  onChange={(e) => setForm({ ...form, rut_persona: e.target.value })}
                  placeholder="Ej: 12.345.678-9"
                  data-testid="input-rut-persona"
                  className={
                    (form.rut_persona || '').trim() && !validarRutChileno((form.rut_persona || '').trim())
                      ? 'border-red-400 focus-visible:ring-red-400'
                      : ''
                  }
                />
                {(form.rut_persona || '').trim() && (
                  validarRutChileno((form.rut_persona || '').trim()) ? (
                    <p className="text-[11px] text-emerald-600 mt-1" data-testid="rut-persona-valido">
                      RUT válido → se guardará como {formatearRut((form.rut_persona || '').trim()) || form.rut_persona}
                    </p>
                  ) : (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="rut-persona-invalido">
                      RUT inválido: el dígito verificador no cumple módulo 11.
                    </p>
                  )
                )}
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold">Habilitación por contexto</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 border rounded-md p-3">
                {FLAGS.map((f) => (
                  <label key={f.key} className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                      className="mt-1 accent-indigo-600"
                      data-testid={`chk-${f.key}`}
                    />
                    <div>
                      <div className="font-medium">{f.label}</div>
                      <div className="text-xs text-muted-foreground">{f.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Iter 286.h — Mapeo con Alegra */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-sm font-semibold">Mapeo Alegra</Label>
                  <p className="text-xs text-muted-foreground">
                    Cuando este medio se use para pagar, Alegra registrará el pago con este método y cuenta bancaria.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={refreshAlegraAccounts}
                  disabled={loadingAlegra}
                  data-testid="btn-refresh-alegra-accounts"
                >
                  {loadingAlegra ? 'Cargando…' : 'Refrescar cuentas Alegra'}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Método de Pago Alegra</Label>
                  <Select
                    value={form.alegra_payment_method || '__none__'}
                    onValueChange={(v) => setForm({ ...form, alegra_payment_method: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger data-testid="input-alegra-payment-method"><SelectValue placeholder="— Sin mapeo —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin mapeo —</SelectItem>
                      {alegraPaymentMethods.map((pm) => (
                        <SelectItem key={pm.codigo} value={pm.codigo}>{pm.label} <span className="text-xs text-muted-foreground ml-1">({pm.codigo})</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cuenta Bancaria Alegra</Label>
                  <Select
                    value={form.alegra_bank_account_id ? String(form.alegra_bank_account_id) : '__none__'}
                    onValueChange={(v) => {
                      if (v === '__none__') {
                        setForm({ ...form, alegra_bank_account_id: null, alegra_bank_account_name: '' });
                      } else {
                        const acc = alegraBankAccounts.find((a) => String(a.id) === v);
                        setForm({
                          ...form,
                          alegra_bank_account_id: acc ? Number(acc.id) : Number(v),
                          alegra_bank_account_name: acc ? acc.name : '',
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="input-alegra-bank-account"><SelectValue placeholder="— Sin mapeo —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin mapeo —</SelectItem>
                      {alegraBankAccounts.map((a) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {a.name} <span className="text-xs text-muted-foreground ml-1">#{a.id}{a.type ? ` · ${a.type}` : ''}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(form.alegra_payment_method && !form.alegra_bank_account_id) || (!form.alegra_payment_method && form.alegra_bank_account_id) ? (
                <p className="text-xs text-orange-600 mt-2">
                  ⚠ El mapeo Alegra está incompleto. Debes completar ambos campos para que se aplique automáticamente.
                </p>
              ) : null}
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                rows={2}
                data-testid="input-observaciones"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="btn-cancelar">Cancelar</Button>
            <Button onClick={handleSave} className="gap-2" data-testid="btn-guardar">
              <Save className="h-4 w-4" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
